import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, IndianRupee, CreditCard, Loader2, CheckCircle, Clock, 
  AlertCircle, Wallet, BadgeCheck, AlertTriangle, Eye, Receipt 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentFees() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [feeSummary, setFeeSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [pendingDues, setPendingDues] = useState([]);
  const [gatewayConfig, setGatewayConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedDue, setSelectedDue] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const token = localStorage.getItem('studentToken');
      const response = await fetch(`${API_URL}/api/student-portal/college-info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCollegeInfo({
          ...data,
          college_name: data.name || data.college_name,
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchFeeData = useCallback(async (retryCount = 0) => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const [summaryRes, ledgerRes, installmentRes, duesRes, gatewayRes] = await Promise.all([
        fetch(`${API_URL}/api/student-portal/fees/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/fees/ledger`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/fees/installments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/fees/pending-dues`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/payment/gateway-config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      // Only update state if we got valid responses
      // Don't clear existing data on partial failures
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        if (summaryData) setFeeSummary(summaryData);
      } else if (summaryRes.status === 401) {
        // Token expired - redirect to login
        localStorage.removeItem('studentToken');
        navigate('/student/login');
        return;
      }
      
      if (ledgerRes.ok) {
        const data = await ledgerRes.json();
        if (data && Array.isArray(data.ledger)) {
          setLedger(data.ledger);
        }
      }
      
      if (installmentRes.ok) {
        const data = await installmentRes.json();
        if (data && Array.isArray(data.installments)) {
          setInstallments(data.installments);
        }
      }
      
      if (duesRes.ok) {
        const data = await duesRes.json();
        if (data && Array.isArray(data.dues)) {
          setPendingDues(data.dues);
        }
      }
      
      if (gatewayRes.ok) {
        const gatewayData = await gatewayRes.json();
        if (gatewayData) setGatewayConfig(gatewayData);
      }
    } catch (error) {
      console.error('Error fetching fee data:', error);
      // Retry up to 2 times on network errors
      if (retryCount < 2) {
        setTimeout(() => fetchFeeData(retryCount + 1), 1000);
        return;
      }
      toast.error('Failed to load fee data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchFeeData();
  }, [navigate, fetchFeeData]);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayNow = (due) => {
    if (!gatewayConfig?.razorpay_enabled) {
      toast.error('Online payment is not enabled. Please contact admin.');
      return;
    }
    setSelectedDue(due);
    setPaymentAmount(due.amount.toString());
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (selectedDue?.type === 'installment' && amount !== selectedDue.amount) {
      toast.error('Installment must be paid in full');
      return;
    }

    setIsProcessing(true);
    const token = localStorage.getItem('studentToken');

    try {
      // Load Razorpay
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay. Please try again.');
      }

      // Create order
      const orderRes = await fetch(`${API_URL}/api/student-portal/payment/create-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amount,
          due_id: selectedDue?.id || 'partial'
        })
      });

      if (!orderRes.ok) {
        const error = await orderRes.json();
        throw new Error(error.detail || 'Failed to create order');
      }

      const orderData = await orderRes.json();

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: collegeInfo?.college_name || collegeInfo?.name || 'Institution',
        description: selectedDue?.description || 'Fee Payment',
        order_id: orderData.gateway_order_id,
        prefill: orderData.prefill,
        theme: {
          color: '#4F46E5'
        },
        handler: async function(response) {
          // Verify payment
          try {
            const verifyRes = await fetch(`${API_URL}/api/student-portal/payment/verify`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                order_id: orderData.order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            const verifyData = await verifyRes.json();
            
            if (verifyRes.ok && verifyData.success) {
              toast.success(`Payment successful! Receipt: ${verifyData.receipt_no}`);
              setShowPaymentModal(false);
              setSelectedDue(null);
              fetchFeeData(); // Refresh data
            } else {
              toast.error(verifyData.detail || 'Payment verification failed');
            }
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setIsProcessing(false);

    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed');
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const totalDue = feeSummary?.due || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-semibold">Fee Details</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Fee Summary Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5" /> Fee Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Total Fee</p>
                <p className="text-2xl font-bold text-gray-900">₹{(feeSummary?.total_fee || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-2xl font-bold text-green-600">₹{(feeSummary?.total_paid || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-500">Seat Booking</p>
                <p className="text-2xl font-bold text-blue-600">₹{(feeSummary?.seat_booking_paid || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <p className="text-sm text-gray-500">Admission Fee</p>
                <p className="text-2xl font-bold text-indigo-600">₹{(feeSummary?.admission_fee_paid || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-500">Due Amount</p>
                <p className="text-2xl font-bold text-red-600">₹{totalDue.toLocaleString('en-IN')}</p>
              </div>
            </div>
            {/* Show discount/scholarship/fine if applicable */}
            {(feeSummary?.discount > 0 || feeSummary?.scholarship > 0 || feeSummary?.late_fine > 0) && (
              <div className="border-t pt-4 mt-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {feeSummary?.discount > 0 && (
                    <div className="flex justify-between p-2 bg-orange-50 rounded">
                      <span className="text-gray-600">Discount:</span>
                      <span className="font-medium text-orange-600">-₹{feeSummary.discount.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {feeSummary?.scholarship > 0 && (
                    <div className="flex justify-between p-2 bg-purple-50 rounded">
                      <span className="text-gray-600">Scholarship:</span>
                      <span className="font-medium text-purple-600">-₹{feeSummary.scholarship.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {feeSummary?.installment_paid > 0 && (
                    <div className="flex justify-between p-2 bg-teal-50 rounded">
                      <span className="text-gray-600">Installment Paid:</span>
                      <span className="font-medium text-teal-600">₹{feeSummary.installment_paid.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {feeSummary?.late_fine > 0 && (
                    <div className="flex justify-between p-2 bg-amber-50 rounded border border-amber-200">
                      <span className="text-gray-600">Late Fine:</span>
                      <span className="font-medium text-amber-600">+₹{feeSummary.late_fine.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between p-2 bg-red-100 rounded border border-red-200">
                    <span className="text-gray-700 font-medium">Net Payable (Due):</span>
                    <span className="font-bold text-red-600">₹{(feeSummary?.due || 0).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Installment Payment Info - Show when installments are set up */}
        {totalDue > 0 && gatewayConfig?.razorpay_enabled && installments.length > 0 && (
          <Card className="mb-6 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <Wallet className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Pay Your Installments Online</h3>
                    <p className="text-sm text-gray-600">Pay installment-wise via Razorpay from the schedule below</p>
                    {gatewayConfig?.is_test_mode && (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded mt-1">
                        <AlertTriangle className="w-3 h-3" /> Test Mode
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Due</p>
                  <p className="text-xl font-bold text-red-600">₹{totalDue.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card when no installments but dues exist */}
        {totalDue > 0 && gatewayConfig?.razorpay_enabled && installments.length === 0 && (
          <Card className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Wallet className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Fee Payment</h3>
                    <p className="text-sm text-gray-600">Contact office for installment setup or pay fines online</p>
                    {gatewayConfig?.is_test_mode && (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded mt-1">
                        <AlertTriangle className="w-3 h-3" /> Test Mode
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Due</p>
                  <p className="text-xl font-bold text-red-600">₹{totalDue.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          <Button 
            variant={activeTab === 'summary' ? 'default' : 'outline'}
            onClick={() => setActiveTab('summary')}
            size="sm"
          >
            Payment History
          </Button>
          <Button 
            variant={activeTab === 'installments' ? 'default' : 'outline'}
            onClick={() => setActiveTab('installments')}
            size="sm"
          >
            Installments
          </Button>
          <Button 
            variant={activeTab === 'pending' ? 'default' : 'outline'}
            onClick={() => setActiveTab('pending')}
            size="sm"
          >
            Pending Dues
            {pendingDues.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">
                {pendingDues.length}
              </span>
            )}
          </Button>
        </div>

        {/* Ledger Tab */}
        {activeTab === 'summary' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Payment History</span>
                <Button variant="outline" size="sm" onClick={() => navigate('/student/receipts')}>
                  <Receipt className="w-4 h-4 mr-1" />
                  All Receipts
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ledger.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No payment records found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Description</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Receipt No</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Mode</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Amount</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {ledger.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{item.date}</td>
                          <td className="px-4 py-3 text-sm font-medium">{item.description}</td>
                          <td className="px-4 py-3 text-sm text-blue-600">{item.receipt_no}</td>
                          <td className="px-4 py-3 text-sm">
                            {item.payment_mode?.includes('Razorpay') ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <BadgeCheck className="w-4 h-4" /> {item.payment_mode}
                              </span>
                            ) : item.payment_mode}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-green-600">
                            ₹{item.amount?.toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {item.receipt_no && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate(`/student/receipt/${item.receipt_no}`)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Installments Tab */}
        {activeTab === 'installments' && (
          <Card>
            <CardHeader>
              <CardTitle>Installment Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              {installments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No installment schedule found</p>
              ) : (
                <div className="space-y-4">
                  {installments.map((inst, idx) => {
                    const isOverdue = inst.status !== 'Paid' && new Date(inst.due_date) < new Date();
                    const fineAmount = inst.fine_amount || 0;
                    const balanceAmount = inst.balance || inst.balance_amount || (inst.amount - (inst.paid_amount || 0));
                    const totalDueAmount = inst.total_due || (balanceAmount + fineAmount);
                    
                    return (
                      <div key={idx} className={`p-4 rounded-lg border ${
                        inst.status === 'Paid' ? 'bg-green-50 border-green-200' :
                        isOverdue ? 'bg-red-50 border-red-200' :
                        'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {inst.status === 'Paid' ? (
                              <CheckCircle className="w-6 h-6 text-green-500" />
                            ) : isOverdue ? (
                              <AlertCircle className="w-6 h-6 text-red-500" />
                            ) : (
                              <Clock className="w-6 h-6 text-gray-400" />
                            )}
                            <div>
                              <p className="font-medium">{inst.installment_name || `Installment ${idx + 1}`}</p>
                              <p className="text-sm text-gray-500">Due: {inst.due_date}</p>
                              {/* Show Late Fine if applicable */}
                              {isOverdue && fineAmount > 0 && inst.status !== 'Paid' && (
                                <p className="text-sm text-red-600 font-medium mt-1">
                                  ⚠️ Late Fine: ₹{fineAmount.toLocaleString('en-IN')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-4">
                            <div>
                              <p className="text-lg font-bold">₹{inst.amount?.toLocaleString('en-IN')}</p>
                              {/* Show Total Due with Fine */}
                              {isOverdue && fineAmount > 0 && inst.status !== 'Paid' && (
                                <p className="text-sm text-red-600 font-semibold">
                                  Total Due: ₹{totalDueAmount.toLocaleString('en-IN')}
                                </p>
                              )}
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                inst.status === 'Paid' ? 'bg-green-100 text-green-700' :
                                isOverdue ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {inst.status || 'Pending'}
                              </span>
                            </div>
                            {inst.status !== 'Paid' && gatewayConfig?.razorpay_enabled && (
                              <Button 
                                size="sm"
                                onClick={() => handlePayNow({
                                  id: inst.id,
                                  type: 'installment',
                                  description: inst.installment_name || `Installment ${idx + 1}`,
                                  amount: totalDueAmount  // Pay total including fine
                                })}
                              >
                                Pay ₹{totalDueAmount.toLocaleString('en-IN')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pending Dues Tab */}
        {activeTab === 'pending' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Pending Dues
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Total Due Amount - Display Only (No Pay Button) */}
              {totalDue > 0 && (
                <div className="mb-6 p-4 rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-full">
                        <Clock className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg text-gray-900">Total Due Amount</p>
                        <p className="text-sm text-gray-600">Pay via installments from the Installments tab</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-red-600">₹{totalDue.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                </div>
              )}

              {pendingDues.length === 0 && totalDue === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-green-600 font-medium">All dues are cleared!</p>
                  <p className="text-sm text-gray-500 mt-1">You have no pending payments.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingDues
                    .filter(due => !due.description?.toLowerCase().includes('total due'))
                    .map((due, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      due.is_overdue ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {due.is_overdue ? (
                            <AlertCircle className="w-6 h-6 text-red-500" />
                          ) : (
                            <Clock className="w-6 h-6 text-amber-500" />
                          )}
                          <div>
                            <p className="font-medium">{due.description}</p>
                            {due.due_date && (
                              <p className={`text-sm ${due.is_overdue ? 'text-red-600' : 'text-gray-500'}`}>
                                Due: {due.due_date} {due.is_overdue && '(Overdue)'}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <p className="text-xl font-bold text-gray-900">₹{due.amount?.toLocaleString('en-IN')}</p>
                          {gatewayConfig?.razorpay_enabled && (
                            <Button onClick={() => handlePayNow(due)}>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Pay Now
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {!gatewayConfig?.razorpay_enabled && pendingDues.filter(due => !due.description?.toLowerCase().includes('total due')).length > 0 && (
                    <div className="p-4 bg-gray-100 rounded-lg text-center">
                      <p className="text-gray-600">Online payment is not enabled.</p>
                      <p className="text-sm text-gray-500 mt-1">Please contact the office for payment.</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-indigo-600" />
              Pay Fee Online
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedDue && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Paying for</p>
                <p className="font-semibold text-lg">{selectedDue.description}</p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (₹)
              </label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
                disabled={selectedDue?.type === 'installment'}
              />
              {selectedDue?.type === 'installment' && (
                <p className="text-xs text-gray-500 mt-1">Installment must be paid in full</p>
              )}
            </div>
            
            {gatewayConfig?.is_test_mode && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">Test Mode</span>
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  Use card 4111 1111 1111 1111 for testing
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={processPayment} disabled={isProcessing || !paymentAmount}>
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay ₹{parseFloat(paymentAmount || 0).toLocaleString('en-IN')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
